/**
 * Project the in-memory party into the PokemonSet shape @pkmn/sim wants.
 *
 * Movesets prefer the per-member resolved moves stored at draft-pick time
 * (via data/levelUpMoves.getMovesAtLevel). The hardcoded SPECIES_DEFAULTS
 * fallback covers (a) saves written before Phase 9 M3's follow-up landed
 * and (b) any case where the dex resolution returned the ['tackle'] sentinel
 * — falling through to defaults gives a saner moveset than just Tackle.
 *
 * Ability + gender stay hardcoded per species (also Gen-3 era data;
 * @pkmn/data doesn't surface "level 5 default ability" in a way that's
 * worth the extra await).
 */
import type { PokemonSet } from '@pkmn/sim';
import type { PartyMember } from '../state/runState';
import { getMovesAtLevel } from '../data/levelUpMoves';

const SPECIES_DEFAULTS: Record<
  string,
  { ability: string; moves: string[]; gender: 'M' | 'F' }
> = {
  bulbasaur: { ability: 'overgrow', moves: ['tackle', 'growl'], gender: 'M' },
  charmander: { ability: 'blaze', moves: ['scratch', 'growl'], gender: 'M' },
  squirtle: { ability: 'torrent', moves: ['tackle', 'tailwhip'], gender: 'M' },
  pidgey: { ability: 'keeneye', moves: ['tackle', 'sandattack'], gender: 'M' },
  rattata: { ability: 'runaway', moves: ['tackle', 'tailwhip'], gender: 'M' },
};

const FALLBACK = { ability: '', moves: ['tackle'], gender: 'M' as const };

/** Stats-integrity (2026-08-02, user ruling: "we definitely want them
 *  to work"): NON-player Pokémon play a FAIR fixed block — 15 IVs
 *  across + neutral Hardy. Player IVs roll uniform 0-31 (mean ~15.5),
 *  so this preserves relative difficulty versus the pre-integrity
 *  build where BOTH sides secretly played perfect 31s. Deliberately
 *  NOT rolled: wild IV rolls would consume seeded draws and churn the
 *  encounter-determinism byte order. Revisit if wilds feel samey. */
export const OPPONENT_IVS = {
  hp: 15,
  atk: 15,
  def: 15,
  spa: 15,
  spd: 15,
  spe: 15,
} as const;
export const OPPONENT_NATURE = 'Hardy';

/** runState Gender → sim GenderName. Unknown values fall back to the
 *  species default in buildSet. */
const GENDER_TO_SIM: Record<string, 'M' | 'F' | 'N'> = {
  male: 'M',
  female: 'F',
  genderless: 'N',
};

export function teamFromParty(party: readonly PartyMember[]): PokemonSet[] {
  // The rolled stats MUST reach the sim — the card's ability, nature,
  // IVs, and gender are what the battle actually plays (ability since
  // 8ede1ca; nature/IVs/gender since the stats-integrity ruling).
  // Nature is stored lowercase ('adamant'); the sim normalizes via
  // toID, but we capitalize to match the PokemonSet convention. The
  // sim's HP math from these IVs equals stored hp.max because
  // computeMaxHp now takes the same rolled IV — the load-bearing
  // equality for HP injection + orb rings, pinned against the real
  // sim in stats-integrity.test.ts.
  return party.map((m) => {
    const set = buildSet(m.species, m.level, m.moves, m.ability);
    set.nature = m.nature.charAt(0).toUpperCase() + m.nature.slice(1);
    set.ivs = { ...m.ivs };
    set.gender = GENDER_TO_SIM[m.gender] ?? set.gender;
    return set;
  });
}

/** Opponent set for a trainer-party member — fair block + the trainer's
 *  explicit cartridge moves. Replaces the inline 31-IV builder that
 *  lived in WorldScene.launchTrainerBattle. */
export function trainerPokemonSet(
  species: string,
  level: number,
  moves: string[],
): PokemonSet {
  return buildSet(species, level, moves);
}

/** Sync wild Pokémon — uses SPECIES_DEFAULTS moves only. Kept for paths
 *  that can't be made async (e.g. legacy callers); prefer wildPokemonAsync. */
export function wildPokemon(species: string, level: number): PokemonSet {
  return buildSet(species, level);
}

/**
 * Async wild Pokémon set — resolves level-appropriate moves via
 * getMovesAtLevel. The extra await is cheap after the first call (loadGen3
 * caches the dex Promise), and ensures wild encounters use cartridge-
 * accurate movesets (e.g. Lv 2 Poochyena genuinely only knows Tackle —
 * the hardcoded SPECIES_DEFAULTS were wrong about that).
 */
export async function wildPokemonAsync(species: string, level: number): Promise<PokemonSet> {
  const moves = await getMovesAtLevel(species, level);
  return buildSet(species, level, moves);
}

function buildSet(
  species: string,
  level: number,
  moves?: string[],
  ability?: string,
): PokemonSet {
  const defaults = SPECIES_DEFAULTS[species] ?? FALLBACK;
  const resolvedMoves = moves && moves.length > 0 ? moves : defaults.moves;
  return {
    name: species[0]!.toUpperCase() + species.slice(1),
    species,
    item: '',
    ability: ability || defaults.ability,
    moves: resolvedMoves,
    nature: OPPONENT_NATURE,
    evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs: { ...OPPONENT_IVS },
    level,
    gender: defaults.gender,
  };
}

/** Mapping for sprite preload. Mirrors packages/sprite-fetch's KANTO_NAMES
 *  list — full Kanto National Dex (#1-151). Names are @pkmn/sim-normalized
 *  (lowercase, no hyphens/apostrophes): nidoranf, nidoranm, mrmime,
 *  farfetchd. */
const KANTO_NAMES: readonly string[] = [
  'bulbasaur', 'ivysaur', 'venusaur', 'charmander', 'charmeleon',
  'charizard', 'squirtle', 'wartortle', 'blastoise', 'caterpie',
  'metapod', 'butterfree', 'weedle', 'kakuna', 'beedrill',
  'pidgey', 'pidgeotto', 'pidgeot', 'rattata', 'raticate',
  'spearow', 'fearow', 'ekans', 'arbok', 'pikachu',
  'raichu', 'sandshrew', 'sandslash', 'nidoranf', 'nidorina',
  'nidoqueen', 'nidoranm', 'nidorino', 'nidoking', 'clefairy',
  'clefable', 'vulpix', 'ninetales', 'jigglypuff', 'wigglytuff',
  'zubat', 'golbat', 'oddish', 'gloom', 'vileplume',
  'paras', 'parasect', 'venonat', 'venomoth', 'diglett',
  'dugtrio', 'meowth', 'persian', 'psyduck', 'golduck',
  'mankey', 'primeape', 'growlithe', 'arcanine', 'poliwag',
  'poliwhirl', 'poliwrath', 'abra', 'kadabra', 'alakazam',
  'machop', 'machoke', 'machamp', 'bellsprout', 'weepinbell',
  'victreebel', 'tentacool', 'tentacruel', 'geodude', 'graveler',
  'golem', 'ponyta', 'rapidash', 'slowpoke', 'slowbro',
  'magnemite', 'magneton', 'farfetchd', 'doduo', 'dodrio',
  'seel', 'dewgong', 'grimer', 'muk', 'shellder',
  'cloyster', 'gastly', 'haunter', 'gengar', 'onix',
  'drowzee', 'hypno', 'krabby', 'kingler', 'voltorb',
  'electrode', 'exeggcute', 'exeggutor', 'cubone', 'marowak',
  'hitmonlee', 'hitmonchan', 'lickitung', 'koffing', 'weezing',
  'rhyhorn', 'rhydon', 'chansey', 'tangela', 'kangaskhan',
  'horsea', 'seadra', 'goldeen', 'seaking', 'staryu',
  'starmie', 'mrmime', 'scyther', 'jynx', 'electabuzz',
  'magmar', 'pinsir', 'tauros', 'magikarp', 'gyarados',
  'lapras', 'ditto', 'eevee', 'vaporeon', 'jolteon',
  'flareon', 'porygon', 'omanyte', 'omastar', 'kabuto',
  'kabutops', 'aerodactyl', 'snorlax', 'articuno', 'zapdos',
  'moltres', 'dratini', 'dragonair', 'dragonite', 'mewtwo',
  'mew',
];

export const SPECIES_TO_DEX: Record<string, number> = Object.fromEntries(
  KANTO_NAMES.map((name, i) => [name, i + 1]),
);
